import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, MapPin, Heart, Sparkles, Users, Calendar, Star, MessageCircle, Home as HomeIcon, Music, Brain, Waves, Instagram, Facebook } from 'lucide-react';

const BOOKING_URL = 'https://book.abbamassage.com/?aff=29';
const WHATSAPP_URL = 'https://wa.me/message/AIKG7VFJUUNMJ1';

// Official Google Maps link
const GOOGLE_MAPS_URL = 'https://maps.app.goo.gl/G2JL5nKRZdTESBVXA';

const SOCIAL_LINKS = {
  instagram: 'https://www.instagram.com/abbamassage',
  facebook: 'https://www.facebook.com/abbamassage',
  google: 'https://maps.app.goo.gl/G2JL5nKRZdTESBVXA',
  tripadvisor: 'https://www.tripadvisor.com.mx/Attraction_Review-g150807-d34151326-Reviews-Abba_Massage_Studio_and_Wellness_Center-Cancun_Yucatan_Peninsula.html'
};

// Language-specific review images
const REVIEW_IMAGES = {
  en: ['https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/9kndjtzi_Review%20Terresa%20English.jpeg'],
  es: [
    'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/sc3nr2et_Rese%C3%B1a%20josalyn%20espanol.jpeg',
    'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/qz81664y_Rese%C3%B1a%20Terresa%20espa%C3%B1ol.jpeg'
  ]
};

const IMAGES = {
  inHome: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/36p207b8_abba-masaje-a-domicilio-horizontal.png',
  studio: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/7hpgtt5m_masaje%20en%20estudio.png',
  soundHealing: 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/vkqrk8xy_singing%20bowls.png',
  reiki: 'https://customer-assets.emergentagent.com/job_5bf56e5f-42e0-4cfb-a653-edc31a7a571c/artifacts/jbr2lcfd_Miri%20Reiki.JPG',
  events: 'https://customer-assets.emergentagent.com/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/c3wz2s4x_ChatGPT%20Image%208%20mar%202026%2C%2012_18_30%20a.m..png',
  team: 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/15z1ydle_TERAPEUTAS%20ABBA.JPG',
  janzu: 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/uvlaxb1p_Janzu.jpg',
  beach: 'https://customer-assets.emergentagent.com/job_5bf56e5f-42e0-4cfb-a653-edc31a7a571c/artifacts/v9wcmpub_in%20home%203.JPG',
  hero: 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/6jsozhxb_HEALING%20OIL%20ON%20ANNA.jpg',
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Quick navigation items - English
const quickNavItemsEn = [
  { label: 'In-Home Massage', href: '/in-home-massage', icon: HomeIcon },
  { label: 'Studio Massage', href: '/studio-services', icon: Heart },
  { label: 'Reiki', href: '/energy-work', icon: Sparkles },
  { label: 'Sound Healing', href: '/energy-work', icon: Music },
  { label: 'Janzu', href: '/janzu', icon: Waves },
  { label: 'Events', href: '/events', icon: Calendar },
];

// Quick navigation items - Spanish (includes Constelaciones Familiares)
const quickNavItemsEs = [
  { label: 'Masaje a Domicilio', href: '/in-home-massage', icon: HomeIcon },
  { label: 'Masaje en Estudio', href: '/studio-services', icon: Heart },
  { label: 'Reiki', href: '/energy-work', icon: Sparkles },
  { label: 'Regresiones e Hipnosis', href: '/hipnosis', icon: Brain },
  { label: 'Sanación con Sonido', href: '/energy-work', icon: Music },
  { label: 'Janzu', href: '/janzu', icon: Waves },
  { label: 'Constelaciones Familiares', href: '/constelaciones', icon: Users },
  { label: 'Eventos', href: '/events', icon: Calendar },
];

export const Home = () => {
  const { t, language } = useLanguage();
  
  // Use language-specific quick nav items
  const quickNavItems = language === 'es' ? quickNavItemsEs : quickNavItemsEn;

  const services = [
    {
      title: t('home.inHomeTitle'),
      description: t('home.inHomeDesc'),
      image: IMAGES.inHome,
      href: '/in-home-massage',
      icon: MapPin
    },
    {
      title: t('home.studioTitle'),
      description: t('home.studioDesc'),
      image: IMAGES.studio,
      href: '/studio-services',
      icon: Heart
    },
    {
      title: t('home.energyTitle'),
      description: t('home.energyDesc'),
      image: IMAGES.soundHealing,
      href: '/energy-work',
      icon: Sparkles
    },
    {
      title: t('home.therapiesTitle'),
      description: t('home.therapiesDesc'),
      image: IMAGES.reiki,
      href: '/hipnosis',
      icon: Heart
    },
    {
      title: t('home.eventsTitle'),
      description: t('home.eventsDesc'),
      image: IMAGES.events,
      href: '/events',
      icon: Calendar
    },
  ].filter((service) => language === 'es' || service.href !== '/hipnosis');
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': 'https://abbamassage.com/#business',
    name: 'ABBA Massage Studio & Wellness Center',
    url: 'https://abbamassage.com/',
    image: IMAGES.hero,
    description: language === 'es'
      ? 'Masajes profesionales y bienestar holístico en Cancún, con servicios en estudio y masaje a domicilio.'
      : 'Professional massage and holistic wellness in Cancun, with studio and in-home massage services.',
    areaServed: [
      'Cancún',
      'Puerto Morelos',
      'Playa del Carmen',
      'Isla Mujeres',
      'Isla Blanca'
    ],
    sameAs: [
      SOCIAL_LINKS.instagram,
      SOCIAL_LINKS.facebook,
      SOCIAL_LINKS.google,
      SOCIAL_LINKS.tripadvisor
    ]
  };
  return (
    <div data-testid="home-page">
    <script
  type="application/ld+json"
  dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
/>
      {/* Hero Section - Static Image Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Static Image Background */}
        <div className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt="ABBA Massage"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-black/50" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto pt-24">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="font-['Playfair_Display'] text-5xl sm:text-6xl lg:text-7xl text-white mb-6 tracking-tight drop-shadow-lg"
            style={{ textShadow: '0 2px 20px rgba(0,0,0,0.4)' }}
          >
            {language === 'es' ? 'Masaje Terapéutico en Cancún' : 'Therapeutic Massage in Cancun'}
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-xl md:text-2xl text-white/95 mb-6 max-w-3xl mx-auto font-['Manrope'] drop-shadow-md"
            style={{ textShadow: '0 1px 10px rgba(0,0,0,0.3)' }}
          >
            {language === 'es' 
              ? 'Masaje a domicilio en Cancún, Isla Mujeres, Isla Blanca, Puerto Morelos y Playa del Carmen — o visita nuestro acogedor estudio en el centro de Cancún.'
              : 'In-home massage in Cancún, Isla Mujeres, Isla Blanca, Puerto Morelos, and Playa del Carmen — or visit our private studio in downtown Cancún.'}
          </motion.p>

          {/* Trust Line */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex items-center justify-center gap-2 mb-10"
          >
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <span className="text-white font-['Manrope'] font-medium">
              {language === 'es' 
                ? 'Calificación perfecta de 5 estrellas en Google y TripAdvisor'
                : 'Perfect 5-Star Rating on Google & TripAdvisor'}
            </span>
          </motion.div>
          
          {/* Dual Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-6 text-base font-['Manrope'] font-semibold shadow-lg hover:scale-105 transition-transform"
                data-testid="hero-book-btn"
              >
                {language === 'es' ? 'Reserva Tu Sesión' : 'Book Your Session'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                className="bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full px-8 py-6 text-base font-['Manrope'] font-semibold shadow-lg hover:scale-105 transition-transform"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle className="mr-2 w-5 h-5" />
                {language === 'es' ? 'Chatea en WhatsApp' : 'Chat on WhatsApp'}
              </Button>
            </a>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1.5 h-3 bg-white/70 rounded-full mt-2"
            />
          </div>
        </motion.div>
      </section>

      {/* Quick Navigation Section */}
      <section className="py-12 px-6 bg-white border-b border-gray-100" data-testid="quick-nav-section">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className={`grid grid-cols-2 sm:grid-cols-4 gap-4 ${language === 'es' ? 'lg:grid-cols-8' : 'lg:grid-cols-7'}`}
          >
            {quickNavItems.map((item, index) => (
              <motion.div key={item.href + index} variants={fadeInUp}>
                <Link 
                  to={item.href}
                  className="flex flex-col items-center p-4 rounded-xl hover:bg-[#F8F6F3] transition-colors group"
                >
                  <div className="w-12 h-12 rounded-full bg-[#F8F6F3] group-hover:bg-[#A91B60] flex items-center justify-center mb-3 transition-colors">
                    <item.icon className="w-5 h-5 text-[#A91B60] group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-sm text-center text-[#1C1C1C] font-['Manrope'] font-medium">
                    {item.label}
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Social Proof / Reviews Section */}
      <section className="py-16 px-6 bg-[#F8F6F3]" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {/* Trust Badge */}
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-8 h-8 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
              </div>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-4">
                {language === 'es' 
                  ? 'Calificación Perfecta de 5 Estrellas' 
                  : 'Perfect 5-Star Rating'}
              </h2>
              <p className="text-lg text-[#5A5A5A] font-['Manrope']">
                {language === 'es' 
                  ? 'en Google y TripAdvisor' 
                  : 'on Google and TripAdvisor'}
              </p>
            </motion.div>

            {/* Social Links - More Prominent */}
            <motion.div variants={fadeInUp} className="flex flex-wrap items-center justify-center gap-6 mb-12">
              <a
                href={SOCIAL_LINKS.google}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-md hover:shadow-lg transition-shadow"
              >
                <Star className="w-6 h-6 text-[#4285F4]" />
                <span className="font-['Manrope'] font-semibold text-[#1C1C1C]">Google Reviews</span>
              </a>
              <a
                href={SOCIAL_LINKS.tripadvisor}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-md hover:shadow-lg transition-shadow"
              >
                <MapPin className="w-6 h-6 text-[#00AF87]" />
                <span className="font-['Manrope'] font-semibold text-[#1C1C1C]">TripAdvisor</span>
              </a>
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-md hover:shadow-lg transition-shadow"
              >
                <Instagram className="w-6 h-6 text-[#E4405F]" />
                <span className="font-['Manrope'] font-semibold text-[#1C1C1C]">Instagram</span>
              </a>
              <a
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-md hover:shadow-lg transition-shadow"
              >
                <Facebook className="w-6 h-6 text-[#1877F2]" />
                <span className="font-['Manrope'] font-semibold text-[#1C1C1C]">Facebook</span>
              </a>
            </motion.div>

            {/* Review Images - Language Specific */}
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-6 max-w-4xl mx-auto">
              {REVIEW_IMAGES[language].map((reviewImg, index) => (
                <img 
                  key={index}
                  src={reviewImg} 
                  alt={`Client Review ${index + 1}`} 
                  className="max-w-sm w-full rounded-2xl shadow-lg"
                />
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Section with Price List Button */}
      <section className="py-24 md:py-32 px-6 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-4 font-['Manrope'] font-semibold">
              {t('home.servicesSubtitle')}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] tracking-tight">
              {t('home.servicesTitle')}
            </motion.h2>
          </motion.div>

          {/* Bento Grid */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {services.map((service, index) => (
              <motion.div
                key={service.href}
                variants={fadeInUp}
                className={`group bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgb(0,0,0,0.06)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-shadow ${
                  index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''
                }`}
              >
                <Link to={service.href} data-testid={`service-card-${index}`}>
                  {/* Image - fully visible, no overlay */}
                  <div className={`relative overflow-hidden ${index === 0 ? 'aspect-[16/10]' : 'aspect-[4/3]'}`}>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  
                  {/* Text below image */}
                  <div className="p-5 md:p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <service.icon className="w-4 h-4 text-[#A91B60]" />
                      <span className="text-xs uppercase tracking-wider text-[#A91B60] font-['Manrope'] font-semibold">
                        {language === 'es' ? 'Explorar' : 'Explore'}
                      </span>
                    </div>
                    <h3 className="font-['Playfair_Display'] text-xl md:text-2xl text-[#1C1C1C] mb-2">
                      {service.title}
                    </h3>
                    <p className="text-sm text-[#5A5A5A] font-['Manrope'] line-clamp-2 mb-4">
                      {service.description}
                    </p>
                    
                    <div className="flex items-center gap-2 text-[#A91B60] text-sm font-['Manrope'] font-semibold group-hover:gap-4 transition-all">
                      {t('cta.learnMore')}
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
          <div className="mt-12 rounded-2xl bg-[#A91B60]/10 p-7 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-testid="celebrations-home-link">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-2 font-['Manrope'] font-semibold">
                {language === 'es' ? 'Momentos para compartir' : 'Moments to share'}
              </p>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1C1C1C] mb-2">
                {language === 'es' ? 'Celebra con ABBA' : 'Celebrate with ABBA'}
              </h2>
              <p className="text-[#5A5A5A] font-['Manrope']">
                {language === 'es'
                  ? 'Cumpleaños, despedidas de soltera, Navidad y encuentros creados a tu medida.'
                  : 'Birthdays, bachelorette celebrations, Christmas, and gatherings planned around you.'}
              </p>
            </div>
            <Link to="/events#celebrations" className="inline-flex items-center gap-2 text-[#A91B60] font-['Manrope'] font-semibold whitespace-nowrap">
              {language === 'es' ? 'Explora las celebraciones' : 'Explore celebrations'} <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 md:py-32 bg-[#C9B99A]/20" data-testid="philosophy-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-4 font-['Manrope'] font-semibold">
                {t('home.philosophy.subtitle')}
              </motion.p>
              <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] mb-6 tracking-tight">
                {t('home.philosophy.title')}
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-[#5A5A5A] leading-relaxed mb-8 font-['Manrope']">
                {t('home.philosophy.description')}
              </motion.p>
              
              <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-4">
                {t('home.philosophy.values').map((value, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#A91B60]" />
                    <span className="text-sm text-[#1C1C1C] font-['Manrope']">{value}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="absolute -top-4 -left-4 w-full h-full bg-[#5BB5B0]/20 rounded-2xl" />
              <img
                src={IMAGES.janzu}
                alt="Janzu Water Therapy"
                className="relative rounded-2xl shadow-xl w-full aspect-[4/3] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Location Section - More Visible */}
      <section className="py-24 md:py-32 px-6 bg-white" data-testid="location-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src={IMAGES.studio}
                alt="ABBA Studio"
                className="rounded-2xl shadow-xl w-full aspect-[4/3] object-cover"
              />
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-4">
                <MapPin className="w-8 h-8 text-[#A91B60]" />
                <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C]">
                  {language === 'es' ? 'Visítanos' : 'Visit Us'}
                </h2>
              </motion.div>
              
              <motion.div variants={fadeInUp} className="mb-6">
                <a 
                  href={GOOGLE_MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group/map"
                >
                  <p className="text-xl text-[#1C1C1C] font-['Manrope'] font-semibold mb-2 group-hover/map:text-[#A91B60] transition-colors">
                    Calle Playa Brava 26, Manzana 1, Lote 53
                  </p>
                  <p className="text-lg text-[#5A5A5A] font-['Manrope'] group-hover/map:text-[#A91B60] transition-colors">
                    Cancún, Quintana Roo<br />
                    CP 77508, México
                  </p>
                </a>
              </motion.div>

              <motion.p variants={fadeInUp} className="text-lg text-[#5A5A5A] leading-relaxed mb-8 font-['Manrope']">
                {language === 'es'
                  ? 'Visita nuestro acogedor estudio en el centro de Cancún para disfrutar de todos nuestros servicios, o reserva una sesión a domicilio y nosotros vamos a ti.'
                  : 'Visit our cozy studio in downtown Cancun for our full range of services, or book an in-home session and we\'ll come to you.'}
              </motion.p>
              
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                    {t('cta.bookNow')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <a 
                  href="https://maps.app.goo.gl/G2JL5nKRZdTESBVXA"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="border-[#A91B60] text-[#A91B60] hover:bg-[#A91B60] hover:text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                    <MapPin className="mr-2 w-5 h-5" />
                    {language === 'es' ? 'Ver en Mapa' : 'See in Maps'}
                  </Button>
                </a>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Host Partnership Section */}
      <section className="py-24 md:py-32 bg-[#A91B60]" data-testid="hosts-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-white/70 mb-4 font-['Manrope'] font-semibold">
                {t('home.hostsSection.subtitle')}
              </motion.p>
              <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-white mb-6 tracking-tight">
                {t('home.hostsSection.title')}
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-white/80 leading-relaxed mb-8 font-['Manrope']">
                {t('home.hostsSection.description')}
              </motion.p>
              
              <motion.div variants={fadeInUp}>
                <Link to="/hosts">
                  <Button className="bg-white hover:bg-gray-100 text-[#A91B60] rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                    {t('home.hostsSection.cta')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src={IMAGES.team}
                alt="ABBA Massage Team"
                className="rounded-2xl shadow-xl w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-[#A91B60]" />
                  <div>
                    <p className="text-2xl font-bold text-[#1C1C1C]">7+</p>
                    <p className="text-sm text-[#5A5A5A]">Certified Therapists</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 bg-[#F8F6F3]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] mb-6 tracking-tight">
              {language === 'es' 
                ? '¿Listo para comenzar tu viaje?' 
                : 'Ready to begin your journey?'}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-[#5A5A5A] mb-10 font-['Manrope']">
              {language === 'es'
                ? 'Reserva tu sesión hoy y experimenta la diferencia del bienestar holístico.'
                : 'Book your session today and experience the difference of holistic wellness.'}
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-10 py-6 font-['Manrope'] font-semibold">
                  {t('cta.bookNow')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full px-10 py-6 font-['Manrope'] font-semibold">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  WhatsApp
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
